import React, { useState, useRef, useCallback } from 'react';
import { X, Paperclip } from 'lucide-react';
import toast from 'react-hot-toast';
import { submitSupportTicket } from '../../utils/api';
import './FeedbackWidget.css';

interface FaceOption {
  key: string;
  label: string;
  svg: string;
  category: string;
  subject: string;
}

const FACES: FaceOption[] = [
  {
    key: 'bug',
    label: 'Report a Bug',
    category: 'bug',
    subject: 'Bug Report',
    svg: `<svg viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <ellipse cx="16" cy="19" rx="7" ry="9"/>
      <line x1="16" y1="10" x2="16" y2="28"/>
      <path d="M9 16h14"/>
      <path d="M9 22h14"/>
      <path d="M12 10c0-2.2 1.8-4 4-4s4 1.8 4 4"/>
      <line x1="5" y1="14" x2="9" y2="16"/>
      <line x1="27" y1="14" x2="23" y2="16"/>
      <line x1="5" y1="24" x2="9" y2="22"/>
      <line x1="27" y1="24" x2="23" y2="22"/>
      <line x1="7" y1="10" x2="10" y2="12"/>
      <line x1="25" y1="10" x2="22" y2="12"/>
    </svg>`,
  },
  {
    key: 'question',
    label: 'Ask a Question',
    category: 'question',
    subject: 'Question',
    svg: `<svg viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="16" cy="16" r="13"/>
      <path d="M12 12a4.5 4.5 0 0 1 7.5 2.5c0 2.5-3.5 2.5-3.5 5"/>
      <circle cx="16" cy="23.5" r="0.5" fill="currentColor"/>
    </svg>`,
  },
  {
    key: 'feedback',
    label: 'Share Feedback',
    category: 'feedback',
    subject: 'Feedback',
    svg: `<svg viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M28 16c0 6.6-5.4 12-12 12-2.2 0-4.3-.6-6-1.6L3 28l1.6-7A11.8 11.8 0 0 1 4 16C4 9.4 9.4 4 16 4s12 5.4 12 12z"/>
      <line x1="10" y1="14" x2="22" y2="14"/>
      <line x1="10" y1="18" x2="18" y2="18"/>
    </svg>`,
  },
  {
    key: 'love',
    label: 'Send Praise',
    category: 'feedback',
    subject: 'Positive Feedback',
    svg: `<svg viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M16 28s-11-6.5-11-14a6 6 0 0 1 6-6c2.5 0 4.2 1.4 5 3 .8-1.6 2.5-3 5-3a6 6 0 0 1 6 6c0 7.5-11 14-11 14z"/>
    </svg>`,
  },
];

const SUPPORT_ICON_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  <circle cx="12" cy="10" r="0" fill="currentColor"/>
  <text x="12" y="14" text-anchor="middle" font-size="12" font-weight="700" fill="currentColor" font-family="serif" stroke="none">?</text>
</svg>`;

export default function FeedbackWidget() {
  const [hovered, setHovered] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [selectedFace, setSelectedFace] = useState<FaceOption | null>(null);
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // Track whether we're animating closed so we can keep content visible during shrink
  const [closing, setClosing] = useState(false);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleMouseEnter = useCallback(() => {
    if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null; }
    if (!expanded) setHovered(true);
  }, [expanded]);

  const handleMouseLeave = useCallback(() => {
    if (!expanded) {
      leaveTimer.current = setTimeout(() => setHovered(false), 500);
    }
  }, [expanded]);

  const handleFaceClick = (face: FaceOption) => {
    setSelectedFace(face);
    setExpanded(true);
    setHovered(false);
  };

  const handleSubmit = async () => {
    if (!message.trim() || !selectedFace) return;
    setSubmitting(true);
    try {
      await submitSupportTicket({
        subject: selectedFace.subject,
        message: message.trim(),
        category: selectedFace.category,
        rating: selectedFace.key,
        pageUrl: window.location.pathname,
        userAgent: navigator.userAgent,
        files: files.length > 0 ? files : undefined,
      });
      toast.success('Thanks for your feedback!');
      handleClose();
    } catch {
      toast.error('Failed to submit.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    // Start shrink animation
    setClosing(true);
    setExpanded(false);
    // After the animation completes, clear state
    setTimeout(() => {
      setClosing(false);
      setSelectedFace(null);
      setMessage('');
      setFiles([]);
      setHovered(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }, 500);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles((prev) => [...prev, ...newFiles].slice(0, 3));
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleBubbleClick = () => {
    if (expanded) {
      handleClose();
    } else {
      setHovered((prev) => !prev);
    }
  };

  const showFormContent = expanded || closing;

  return (
    <div className="feedback-widget">
      <div
        className={`feedback-capsule ${hovered ? 'open' : ''} ${expanded ? 'expanded' : ''} ${closing ? 'closing' : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* The form content (lives inside the capsule, revealed when expanded) */}
        <div className="feedback-capsule-form">
          {showFormContent && selectedFace && (
            <div className="feedback-form">
              <div className="feedback-form-toolbar">
                <div className="feedback-form-picker">
                  <span className="feedback-form-label">{selectedFace.label}</span>
                  <div className="feedback-form-faces">
                    {FACES.map((face) => (
                      <span key={face.key} className="feedback-tooltip-wrap" data-tooltip={face.label}>
                        <button
                          className={`feedback-face-btn in-form ${selectedFace.key === face.key ? 'selected' : ''}`}
                          onClick={() => setSelectedFace(face)}
                          dangerouslySetInnerHTML={{ __html: face.svg }}
                        />
                      </span>
                    ))}
                  </div>
                </div>
                <button className="feedback-close-btn" onClick={handleClose}><X size={16} /></button>
              </div>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us more..."
                rows={3}
                autoFocus
              />
              <div className="feedback-attach-row">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf,.txt,.log"
                  multiple
                  onChange={handleFileChange}
                  className="feedback-file-input"
                />
                <button
                  type="button"
                  className="feedback-attach-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={files.length >= 3}
                  title={files.length >= 3 ? 'Max 3 files' : 'Attach file'}
                >
                  <Paperclip size={14} />
                  <span>Attach{files.length > 0 ? ` (${files.length}/3)` : ''}</span>
                </button>
              </div>
              {files.length > 0 && (
                <div className="feedback-file-list">
                  {files.map((file, i) => (
                    <div key={`${file.name}-${i}`} className="feedback-file-chip">
                      <span className="feedback-file-name">{file.name}</span>
                      <button className="feedback-file-remove" onClick={() => removeFile(i)}>
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                className="feedback-submit-btn"
                onClick={handleSubmit}
                disabled={submitting || !message.trim()}
              >
                {submitting ? 'Sending...' : 'Submit'}
              </button>
            </div>
          )}
        </div>

        {/* Sliding tray with label + icons (visible on hover, hidden when expanded) */}
        <div className="feedback-capsule-inner">
          <span className="feedback-capsule-label">How are we doing?</span>
          <div className="feedback-capsule-faces">
            {FACES.map((face) => (
              <span key={face.key} className="feedback-tooltip-wrap" data-tooltip={face.label}>
                <button
                  className="feedback-face-btn"
                  onClick={() => handleFaceClick(face)}
                  dangerouslySetInnerHTML={{ __html: face.svg }}
                />
              </span>
            ))}
          </div>
        </div>

        {/* The icon button (always visible at bottom-right) */}
        <button
          className={`feedback-capsule-icon ${expanded ? 'active' : ''}`}
          onClick={handleBubbleClick}
          aria-label={expanded ? 'Close feedback' : 'Give feedback'}
          dangerouslySetInnerHTML={{ __html: SUPPORT_ICON_SVG }}
        />
      </div>
    </div>
  );
}
